<template>
	<div class="avod-hero__container">
		<div v-observe-visibility="observeVisibilityOptions" class="avod-hero">
			<!-- Background Image Layer -->
			<div class="avod-hero__image-background">
				<PictureComp
					v-if="imageUrls.length > 0"
					imageProfile="backdrop"
					:imageUrl="imageUrls[0]"
					:alt="titleName"
					:lazy="false"
					fetchpriority="high"
					:object-position-top="true"
				/>
				<img v-else src="/appassets/img/backdrop-placeholder.jpg" class="placeholder__backdrop" />
				<div class="backdrop-gradient"></div>
			</div>

			<!-- Content Overlay (bottom) -->
			<div class="avod-hero__content">
				<!-- Centered Play Button -->
				<div class="avod-hero__play-button" @click="handlePlayClick">
					<JustWatchTVPlayBtn />
				</div>

				<!-- Title Info Row -->
				<div class="avod-hero__info">
					<TitleDetailInfoRow :title="title" :showChartInfo="false" />
				</div>

				<!-- Title -->
				<h1 class="avod-hero__title">{{ titleName }}</h1>

				<!-- Description with Read More -->
				<div class="avod-hero__description">
					<p
						ref="descriptionRef"
						:class="{ 'description-text--expanded': isDescriptionExpanded }"
						class="description-text"
					>
						{{ description }}
					</p>
					<button
						class="avod-hero__read-more"
						:class="{ 'avod-hero__read-more--visible': showReadMoreButton }"
						@click="toggleDescription"
					>
						{{ isDescriptionExpanded ? $t('WEBAPP_SHOW_LESS') : $t('WEBAPP_READ_MORE') }}
					</button>
				</div>
			</div>
		</div>
		<div class="avod-hero__jumplinks">
			<slot></slot>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import JustWatchTVPlayBtn from '@/pages/titleDetailV1/v1/hero/JustwatchTVPlayBtn.vue';
import TitleDetailInfoRow from '@/pages/titleDetailV1/components/titleDetailInfoRow.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { OfferLocation } from '@/helpers/offer-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { MonetizationType, SraPlacement } from '@/@types/graphql-types';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import TrailerModal from '@/components/TrailerModal.vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';

const props = defineProps<{
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
}>();

const languageStore = useLanguageStore();
const appStore = useAppStore();

const titleName = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.title : props.title.content.title;
});

const description = computed(() => {
	return props.title.content.shortDescription || '';
});

const imageUrls = computed(() => props.title.content.backdrops.map(backdrop => backdrop.backdropUrl));

const isJustWatchTV = computed(
	() =>
		props.title.justwatchTVOffers.length > 0 &&
		props.title.justwatchTVOffers[0].monetizationType === MonetizationType.Free &&
		props.title.justwatchTVOffers[0].package.technicalName === 'justwatchtv'
);

const { sponsoredAd } = useSendBidId({
	pageType: 'VIEW_TITLE_DETAIL',
	placement: SraPlacement.BackdropVideo,
});

const titleClips = computed(() => {
	const JustWatchTVClip = isJustWatchTV.value
		? [
				{
					provider: 'JUSTWATCH_TV',
					externalId: props.title.justwatchTVOffers[0].id,
					sourceUrl:
						props.title.justwatchTVOffers[0].streamUrlExternalPlayer ??
						props.title.justwatchTVOffers[0].streamUrl,
				},
		  ]
		: null;

	return JustWatchTVClip || [];
});

// Modal state
const hasModalOpened = ref(false);
const isModalOpening = ref(false);

// Offer for impression tracking
const offerForImpression = computed(() => {
	if (isJustWatchTV.value) return props.title.justwatchTVOffers[0];
	return null;
});

// Clickout URL for tracking
const clickoutUrl = computed(() => {
	if (!offerForImpression.value) return null;

	const objectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const objectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;

	return new ClickoutUrl(offerForImpression.value)
		.setTitleSourceId(props.title?.id ?? null)
		.addContexts(new SnowplowTitleContextGraphql(objectId, objectType))
		.addContexts(props.additionalContexts)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.set('uct_country', languageStore.country)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'video_player')
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('TitleContextGraphql', 'jwRawEntityId', props.title?.id ?? null)
		.setCanary(appStore.xServeTrack);
});

// Play Button Click Handler (same as handleBackdropClick)
function handlePlayClick() {
	if (clickoutUrl.value) {
		trackClickout(clickoutUrl.value);
	}

	openTrailerModal(false);
}

// Open trailer modal
async function openTrailerModal(nonInteraction: boolean) {
	if (titleClips.value.length === 0) return;
	if (hasModalOpened.value || isModalOpening.value) return;
	isModalOpening.value = true;

	try {
		hasModalOpened.value = true;
		await new Promise(resolve => setTimeout(resolve, 0));

		TrackingHelper.trackEvent('youtube_started', {
			action: props.title.objectType.toLowerCase(),
			label: 'backdrop',
			value: 5,
			nonInteraction,
		});

		await ModalHelper.openModal(
			TrailerModal,
			{
				clips: titleClips,
				title: props.title,
				source: 'backdrop',
				additionalContexts: props.additionalContexts,
				playerAdditionalContexts: getClickoutUrlContextsArray(clickoutUrl.value),
				sponsoredAd: sponsoredAd.value,
				sraPlacement: SraPlacement.BackdropVideo,
			},
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[],
			() => (hasModalOpened.value = false)
		);
	} catch (e) {
		console.error(e);
	} finally {
		isModalOpening.value = false;
		hasModalOpened.value = false;
	}
}

// Read More Functionality
const isDescriptionExpanded = ref(false);
const showReadMoreButton = ref(false);
const descriptionRef = ref<HTMLElement | null>(null);

// Visibility tracking
const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

function onVisibilityChanged(isVisible: boolean) {
	if (!!clickoutUrl.value) onIntersection(clickoutUrl.value, isVisible);
}

const observeVisibilityOptions = computed(() => {
	return !!offerForImpression.value
		? {
				callback: onVisibilityChanged,
				...observableOptions,
		  }
		: null;
});

onMounted(() => {
	// Check if description exceeds 3 lines
	if (descriptionRef.value) {
		const isClamped = descriptionRef.value.scrollHeight > descriptionRef.value.clientHeight;
		showReadMoreButton.value = isClamped;
	}
});

function toggleDescription() {
	isDescriptionExpanded.value = !isDescriptionExpanded.value;
	TrackingHelper.trackEvent('userinteraction', {
		action: isDescriptionExpanded.value ? 'read_more_clicked' : 'read_less_clicked',
		label: 'synopsis',
	});
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.avod-hero {
	position: relative;
	width: 100%;
	min-height: calc(100vh - 120px);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;

	@media (max-width: 768px) {
		min-height: calc(85vh - 120px);
	}

	// Background Image (same as TitleDetailHeroV1)
	&__image-background {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 0;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;

			&.placeholder__backdrop {
				object-fit: cover;
			}
		}

		.backdrop-gradient {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: 1;
			background: linear-gradient(
					to top,
					rgba(6, 13, 23, 1) 0%,
					rgba(6, 13, 23, 0.8) 30%,
					rgba(6, 13, 23, 0) 100%
				),
				linear-gradient(to right, rgba(6, 13, 23, 0.8) 0%, rgba(6, 13, 23, 0) 50%);
		}
	}

	// Content Overlay (bottom)
	&__content {
		position: absolute;
		top: 0;
		left: 50%;
		transform: translateX(-50%);
		bottom: 0;
		z-index: 2;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		max-width: 1440px;
		width: 100%;
		padding: 40px 64px 0 65px;

		@media (max-width: 1050px) {
			padding: 32px 32px 0 16px;
		}

		@media (max-width: 600px) {
			padding: 24px 16px 0 16px;
		}
	}

	&__container {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}

	&__jumplinks {
		max-width: 1440px;
		width: 100%;
		padding: 40px 64px 0 65px;

		@media (max-width: 1050px) {
			padding: 32px 32px 0 16px;
		}

		@media (max-width: 600px) {
			padding: 24px 16px 0 16px;
		}
	}

	// Centered Play Button (within content flow)
	&__play-button {
		flex: 1;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	// Title Info Row
	&__info {
		margin-bottom: 16px;
	}

	// Title
	&__title {
		font-family: Anton, Anton-Fallback, Impact;
		font-size: 48px;
		font-weight: 400;
		line-height: 1.2;
		color: white;
		margin: 0 0 16px 0;
		text-transform: uppercase;
		max-width: 600px;

		@media (max-width: 1050px) {
			font-size: 40px;
		}

		@media (max-width: 600px) {
			font-size: 32px;
		}
	}

	// Description
	&__description {
		max-width: 600px;
		margin-bottom: 24px;
	}

	.description-text {
		font-size: 16px;
		line-height: 24px;
		color: #d2d3d5;
		margin: 0 0 8px 0;

		// Use line-clamp for clamping
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		overflow: hidden;

		// When expanded, remove line-clamp
		&--expanded {
			display: block;
			-webkit-line-clamp: unset;
			line-clamp: unset;
		}
	}

	// Read More button - hidden by default with visibility (no CLS)
	&__read-more {
		background: none;
		border: none;
		color: #78a6b8;
		font-size: 14px;
		font-weight: 700;
		cursor: pointer;
		padding: 0;
		text-decoration: underline;
		text-align: left;
		display: block;
		visibility: hidden; // Hidden by default, no layout shift

		// Show button when needed (set by onMounted)
		&--visible {
			visibility: visible;
		}

		&:hover {
			color: #9cb2bc;
		}
	}
}
</style>
